import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, CircleCheck, KeyRound, Terminal } from 'lucide-react';
import { isValidEmail } from '../../shared/utils';
import { AuthLayout } from '../components/layout';
import {
  Alert,
  Button,
  detectTimezone,
  Field,
  Input,
  Spinner,
  TimezoneSelect,
} from '../components/ui';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/query';
import { useSessionStore } from '../stores/session';
import { useWorkspaceStore } from '../stores/workspace';

interface SetupForm {
  setupToken: string;
  name: string;
  email: string;
  workspaceName: string;
  timezone: string;
}

const EMPTY: SetupForm = {
  setupToken: '',
  name: '',
  email: '',
  workspaceName: '',
  timezone: detectTimezone(),
};

/**
 * First-run setup: claims the superadmin account, creates the first workspace
 * and signs the operator in. Guarded by the `SETUP_TOKEN` secret, so it can only
 * ever be run by whoever deployed the Worker.
 */
export function SetupPage() {
  const navigate = useNavigate();
  const refresh = useSessionStore((state) => state.refresh);
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);

  const [form, setForm] = useState<SetupForm>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const setupState = useQuery({
    queryKey: qk.setupState(),
    queryFn: () => api.auth.setupState(),
    staleTime: 0,
    retry: false,
  });

  const claim = useMutation({
    mutationFn: () =>
      api.auth.setup({
        setupToken: form.setupToken.trim(),
        email: form.email.trim(),
        name: form.name.trim(),
        workspaceName: form.workspaceName.trim(),
        timezone: form.timezone,
      }),
    onSuccess: async (response) => {
      setWorkspace(response.workspace.id);
      await refresh();
      navigate('/onboarding', { replace: true });
    },
    onError: (error) => {
      if (error instanceof ApiError) setFieldErrors(error.fieldErrors);
    },
  });

  const update = <Key extends keyof SetupForm>(key: Key, value: SetupForm[Key]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => {
      if (!(key in current)) return current;
      const { [key as string]: _removed, ...rest } = current;
      return rest;
    });
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.setupToken.trim()) errors.setupToken = 'Paste the SETUP_TOKEN secret.';
    if (!form.name.trim()) errors.name = 'Tell us what to call you.';
    if (!isValidEmail(form.email)) errors.email = 'Enter a valid email address.';
    if (!form.workspaceName.trim()) errors.workspaceName = 'Name the first workspace.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const error = claim.error instanceof ApiError ? claim.error : null;
  const generalError = error && error.fields.length === 0 ? error.message : null;

  if (setupState.isLoading) {
    return (
      <AuthLayout title="Checking this deployment" subtitle="One moment.">
        <div className="flex justify-center py-6">
          <Spinner label="Reading setup state" />
        </div>
      </AuthLayout>
    );
  }

  if (setupState.data && !setupState.data.setupRequired) {
    return (
      <AuthLayout
        eyebrow="Already set up"
        title="This deployment is ready"
        subtitle="The first account has already been claimed. Sign in, or ask an owner for an invitation."
      >
        <Button fullWidth size="lg" onClick={() => navigate('/login', { replace: true })}>
          Go to sign in
        </Button>
      </AuthLayout>
    );
  }

  if (setupState.data && !setupState.data.setupTokenConfigured) {
    return (
      <AuthLayout
        eyebrow="First run"
        title="Setup token missing"
        subtitle="Setup cannot proceed until the deployment has a SETUP_TOKEN secret."
      >
        <div className="space-y-4">
          <Alert tone="warning" title="Add the secret, then reload this page" icon={<Terminal />}>
            <p>Generate every required secret at once:</p>
            <pre className="mt-2 overflow-x-auto rounded-md border border-border bg-surface px-3 py-2 font-mono text-[11px] text-fg">
              npm run secrets:bootstrap
            </pre>
            <p className="mt-2">Or set just this one:</p>
            <pre className="mt-2 overflow-x-auto rounded-md border border-border bg-surface px-3 py-2 font-mono text-[11px] text-fg">
              npx wrangler secret put SETUP_TOKEN
            </pre>
          </Alert>
          <Button variant="secondary" fullWidth onClick={() => void setupState.refetch()}>
            Check again
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      eyebrow="First run"
      title="Claim this deployment"
      subtitle="You are about to create the superadmin account and the first workspace. This page only works once."
      footer="Everything here can be changed later in Settings."
    >
      <form
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (validate()) claim.mutate();
        }}
      >
        <AnimatePresence>
          {generalError && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
              <Alert tone="danger" title="Setup could not complete">
                {generalError}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        <Field
          label="Setup token"
          htmlFor="setup-token"
          required
          error={fieldErrors.setupToken ?? null}
          hint="The SETUP_TOKEN secret you configured for this Worker."
        >
          <Input
            id="setup-token"
            type="password"
            autoComplete="off"
            spellCheck={false}
            placeholder="••••••••••••••••"
            leadingIcon={<KeyRound />}
            value={form.setupToken}
            invalid={Boolean(fieldErrors.setupToken)}
            onChange={(event) => update('setupToken', event.target.value)}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Your name" htmlFor="setup-name" required error={fieldErrors.name ?? null}>
            <Input
              id="setup-name"
              autoComplete="name"
              placeholder="Ada Lovelace"
              value={form.name}
              invalid={Boolean(fieldErrors.name)}
              onChange={(event) => update('name', event.target.value)}
            />
          </Field>

          <Field label="Your email" htmlFor="setup-email" required error={fieldErrors.email ?? null}>
            <Input
              id="setup-email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={form.email}
              invalid={Boolean(fieldErrors.email)}
              onChange={(event) => update('email', event.target.value)}
            />
          </Field>
        </div>

        <Field
          label="Workspace name"
          htmlFor="setup-workspace"
          required
          error={fieldErrors.workspaceName ?? null}
          hint="Usually the business you publish for. You can create more later."
        >
          <Input
            id="setup-workspace"
            placeholder="Northwind Coffee"
            value={form.workspaceName}
            invalid={Boolean(fieldErrors.workspaceName)}
            onChange={(event) => update('workspaceName', event.target.value)}
          />
        </Field>

        <Field
          label="Timezone"
          htmlFor="setup-timezone"
          hint="Every schedule in the app is shown in this zone."
        >
          <TimezoneSelect
            id="setup-timezone"
            value={form.timezone}
            onChange={(timezone) => update('timezone', timezone)}
          />
        </Field>

        <Button
          type="submit"
          size="lg"
          fullWidth
          loading={claim.isPending}
          loadingLabel="Creating your workspace"
          rightIcon={<ArrowRight className="size-4" />}
        >
          Create account and workspace
        </Button>

        <ul className="space-y-1.5 border-t border-border pt-4 text-xs text-subtle">
          {[
            'Creates your superadmin user',
            'Creates the first workspace and makes you its owner',
            'Signs you in and starts onboarding',
          ].map((line) => (
            <li key={line} className="flex items-start gap-2">
              <CircleCheck className="mt-0.5 size-3.5 shrink-0 text-success" aria-hidden />
              {line}
            </li>
          ))}
        </ul>
      </form>
    </AuthLayout>
  );
}

export default SetupPage;
